import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';
import { cn } from '~/lib/utils';

type TeamResponse = InferResponseType<(typeof api.api.org)[':orgId']['users']['$get']>;
type Member = TeamResponse['members'][number];
type Role = Member['role'];
type DeptResponse = InferResponseType<(typeof api.api.departments)['$get']>;
type Department = DeptResponse['departments'][number];

const ASSIGNABLE_ROLES: Role[] = ['org_manager', 'dept_manager', 'user'];
const DEPT_MGR_ASSIGNABLE_ROLES: Role[] = ['dept_manager', 'user'];

const ROLE_COLORS: Record<Role, string> = {
  admin: 'text-primary font-medium',
  org_manager: 'text-foreground',
  dept_manager: 'text-foreground',
  user: 'text-muted-foreground'
};

interface TeamListProps {
  orgId: string;
  members: Member[];
  search: string;
  currentRole: Role;
  allDepartments: Department[];
}

export function TeamList({
  orgId,
  members,
  search,
  currentRole,
  allDepartments
}: TeamListProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['team'] });
  };

  const changeRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const res = await api.api.org[':orgId'].users[':userId'].role.$patch({
        param: { orgId, userId },
        json: { role: role as Exclude<Role, 'admin'> }
      });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: invalidate
  });

  const addDepartment = useMutation({
    mutationFn: async ({ userId, departmentId }: { userId: string; departmentId: string }) => {
      const res = await api.api.org[':orgId'].users[':userId'].departments.$post({
        param: { orgId, userId },
        json: { departmentId }
      });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: invalidate
  });

  const removeDepartment = useMutation({
    mutationFn: async ({ userId, deptId }: { userId: string; deptId: string }) => {
      const res = await api.api.org[':orgId'].users[':userId'].departments[':deptId'].$delete({
        param: { orgId, userId, deptId }
      });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: invalidate
  });

  const removeFromOrg = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.api.org[':orgId'].users[':userId'].$delete({
        param: { orgId, userId }
      });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: invalidate
  });

  // Client-side search
  const filtered = search
    ? members.filter(
        (m) =>
          m.name.toLowerCase().includes(search.toLowerCase()) ||
          m.email.toLowerCase().includes(search.toLowerCase()) ||
          m.departmentName.toLowerCase().includes(search.toLowerCase())
      )
    : members;

  // Group by department
  const grouped = new Map<string, { name: string; members: Member[] }>();
  for (const member of filtered) {
    const existing = grouped.get(member.departmentId);
    if (existing) {
      existing.members.push(member);
    } else {
      grouped.set(member.departmentId, { name: member.departmentName, members: [member] });
    }
  }

  if (grouped.size === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {search ? t('team.noResults') : t('team.noMembers')}
      </p>
    );
  }

  const assignableRoles =
    currentRole === 'dept_manager' ? DEPT_MGR_ASSIGNABLE_ROLES : ASSIGNABLE_ROLES;
  const canRemoveFromOrg = currentRole === 'admin' || currentRole === 'org_manager';

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
            {t('team.columns.name')}
          </TableHead>
          <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
            {t('team.columns.email')}
          </TableHead>
          <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
            {t('team.columns.role')}
          </TableHead>
          <TableHead className="w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
            {t('team.columns.actions')}
          </TableHead>
        </TableRow>
      </TableHeader>

      {Array.from(grouped.entries()).map(([deptId, { name, members: deptMembers }]) => (
        <TableBody key={deptId}>
          {/* Department section header */}
          <TableRow className="hover:bg-transparent border-t border-border">
            <TableCell
              colSpan={4}
              className="py-2.5 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30"
            >
              {t('team.department')}: {name}
            </TableCell>
          </TableRow>

          {deptMembers.map((member) => {
            // Departments this user is already in (across all data, not just filtered)
            const userDeptIds = new Set(
              members.filter((m) => m.id === member.id).map((m) => m.departmentId)
            );
            const availableDepts = allDepartments.filter((d) => !userDeptIds.has(d.id));

            return (
              <TableRow key={`${member.id}-${deptId}`} className="group">
                <TableCell className="py-3 text-sm text-foreground">{member.name}</TableCell>
                <TableCell className="py-3 text-sm text-muted-foreground">{member.email}</TableCell>
                <TableCell className={cn('py-3 text-sm', ROLE_COLORS[member.role])}>
                  {t(`team.roles.${member.role}` as const)}
                </TableCell>
                <TableCell className="py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent">
                      <MoreVertical className="size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {/* Change role submenu */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          {t('team.actions.changeRole')}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuRadioGroup
                            value={member.role}
                            onValueChange={(role: string) => {
                              changeRole.mutate({ userId: member.id, role });
                            }}
                          >
                            {assignableRoles.map((r) => (
                              <DropdownMenuRadioItem key={r} value={r}>
                                {t(`team.roles.${r}` as const)}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>

                      {/* Add department submenu */}
                      {availableDepts.length > 0 && (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            {t('team.actions.addDepartment')}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {availableDepts.map((dept) => (
                              <DropdownMenuItem
                                key={dept.id}
                                onClick={() => {
                                  addDepartment.mutate({
                                    userId: member.id,
                                    departmentId: dept.id
                                  });
                                }}
                              >
                                {dept.name}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}

                      {/* Remove from this department */}
                      <DropdownMenuItem
                        onClick={() => {
                          removeDepartment.mutate({ userId: member.id, deptId });
                        }}
                      >
                        {t('team.actions.removeDepartment')}
                      </DropdownMenuItem>

                      {/* Remove from org — admin/org_manager only */}
                      {canRemoveFromOrg && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => {
                              removeFromOrg.mutate(member.id);
                            }}
                          >
                            {t('team.actions.removeFromOrg')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      ))}
    </Table>
  );
}
